/**
 * API client.
 *
 * Same-origin by design: Vite proxies /api to the backend in dev (ports live in
 * the root .env), and FastAPI serves this SPA itself in production. So the
 * session cookie works identically in both, and there is no API base URL to
 * configure here.
 *
 * Phase 0 note: `npm run gen:api` regenerates src/lib/api/schema.d.ts from
 * FastAPI's OpenAPI schema (§4 — the typed client is generated, not written).
 * The hand-written types below are the ones the UI actually consumes; they are
 * checked against the generated schema at build time.
 *
 * Mobile: every path goes through `apiBase()` (src/platform/), which is `""`
 * in a browser — so web requests are unchanged — and the real server inside
 * the Android app, whose pages load from the phone itself.
 */

import {
  forgetAll,
  load as loadSaved,
  NEEDS_CONNECTION,
  offlineEnabled,
  save as saveAnswer,
  servedFresh,
  servedSaved,
} from "@/lib/offline";
import { apiBase, deviceName } from "@/platform";
import { authToken, nativeRequestHeaders, setAuthToken } from "@/platform/session";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * In a browser: JSON's content type when there is a body, otherwise nothing —
 * exactly the headers this client always sent. In the Android app, also what
 * it is and its sign-in token (`platform/session.ts`).
 */
function requestHeaders(json: boolean): Record<string, string> | undefined {
  const headers = { ...(json ? { "Content-Type": "application/json" } : {}), ...nativeRequestHeaders() };
  return Object.keys(headers).length ? headers : undefined;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const read = method === "GET";
  const installed = offlineEnabled();

  // In the installed apps a write never leaves with no connection (M5): it
  // asks the server to decide something, and a phone can't stand in for that.
  if (installed && !read && typeof navigator !== "undefined" && navigator.onLine === false) {
    throw new ApiError(0, NEEDS_CONNECTION);
  }

  let res: Response;
  try {
    res = await fetch(apiBase() + path, {
      credentials: "same-origin",
      headers: requestHeaders(Boolean(init?.body)),
      ...init,
    });
  } catch {
    // The installed apps show the last good answer, dated (lib/offline.ts).
    if (installed && read) {
      const saved = loadSaved(path);
      if (saved) {
        servedSaved(saved.at);
        return saved.data as T;
      }
    }
    if (installed && !read) throw new ApiError(0, NEEDS_CONNECTION);
    // Network-level failure — almost always "the backend isn't running" in dev.
    // Say that, rather than surfacing a bare TypeError (§13.1 error states).
    throw new ApiError(0, "Can't reach the MyDeck server. Is the backend running?");
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? safeJson(text) : undefined;

  if (!res.ok) {
    // A refused session takes its saved figures with it.
    if (res.status === 401) forgetAll();
    throw new ApiError(res.status, extractDetail(body) ?? res.statusText, body);
  }
  if (read && installed) {
    servedFresh();
    saveAnswer(path, body);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** FastAPI puts errors in `detail` — a string, or a validation-error array. */
function extractDetail(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        const loc = Array.isArray(e?.loc) ? e.loc.slice(1).join(".") : "";
        return loc ? `${loc}: ${e.msg}` : e.msg;
      })
      .join("; ");
  }
  return null;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "DELETE", body: body ? JSON.stringify(body) : undefined }),
};

// --- Types -------------------------------------------------------------------
// Money is `string` everywhere, matching the API contract (§4). Never `number`.

export interface CardTheme {
  gradient_from: string;
  gradient_to: string;
  foreground: string;
}

export interface Card {
  id: string;
  issuer: string;
  product_name: string | null;
  network: string | null;
  last4: string;
  nickname: string | null;
  theme: CardTheme | null;
  rewards_note: string | null;
  billing_day: number | null;
  due_day: number | null;
  credit_limit: string | null;
  billing_day_pinned: boolean;
  due_day_pinned: boolean;
  credit_limit_pinned: boolean;
  status: "active" | "closed";
  source: "manual" | "discovered";
  has_pdf_password: boolean;
  created_at: string;
  updated_at: string;
}

export interface CardInput {
  issuer: string;
  product_name?: string | null;
  network?: string | null;
  last4: string;
  nickname?: string | null;
  theme?: CardTheme | null;
  rewards_note?: string | null;
  billing_day?: number | null;
  due_day?: number | null;
  credit_limit?: string | null;
  pdf_password?: string | null;
}

// --- Build status (temporary — deleted with routes/Status.tsx) ---------------

export interface BuildStatus {
  generated_at: string;
  cards: number;
  active_cards: number;
  statements: number;
  transactions: number;
  provisional_transactions: number;
  categorized_transactions: number;
  txn_rules: number;
  merchant_rules: number;
  open_review_items: number;
  review_by_type: Record<string, number>;
  emails_processed: number;
  alerts_pending: number;
  last_sync_at: string | null;
  last_sync_ok: boolean | null;
  fiscal_year: string;
  fiscal_years: string[];
  real_cashback: string;
  clawbacks: string;
  lifetime_real_cashback: string;
  benefits_total: string;
  unexplained_count: number;
  unexplained_total: string;
  /** Issuers with an alert parser, read from the registry — never a list
   *  written down here (§2.1 forbids issuer names in application code). */
  alert_parsers: string[];
  cards_without_statements: {
    card_id: string;
    name: string;
    last4: string;
    statements: number;
    transactions: number;
  }[];
}

export const statusApi = {
  get: () => api.get<BuildStatus>("/api/status"),
};

export type SenderKind = "statement" | "alert" | "other";

export interface SenderMapping {
  id: string;
  address_pattern: string;
  issuer: string;
  kind: SenderKind;
  active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SenderMappingInput {
  address_pattern: string;
  issuer: string;
  kind: SenderKind;
  active?: boolean;
  notes?: string | null;
}

export interface AppInfo {
  app_name: string;
  env: string;
  auth_enabled: boolean;
  email_mode: "imap" | "gmail_api" | "disabled";
  fiscal_year: string;
  card_count: number;
  onboarding_complete: boolean;
}

export interface Health {
  status: string;
  app: string;
  version: string;
  env: string;
  database: string;
  database_ok: boolean;
  migrations_current: boolean;
  migration_head: string | null;
  email_mode: string;
  auth_enabled: boolean;
  time: string;
}

export interface SenderTestResult {
  matched: boolean;
  issuer?: string;
  kind?: SenderKind;
  pattern?: string;
  explanation: string;
}

// --- Endpoints ---------------------------------------------------------------

export const cardsApi = {
  list: (includeClosed = false) =>
    api.get<Card[]>(`/api/cards?include_closed=${includeClosed}`),
  get: (id: string) => api.get<Card>(`/api/cards/${id}`),
  create: (input: CardInput) => api.post<Card>("/api/cards", input),
  update: (id: string, input: Partial<CardInput> & { status?: string }) =>
    api.patch<Card>(`/api/cards/${id}`, input),
  close: (id: string) => api.post<Card>(`/api/cards/${id}/close`),
  reopen: (id: string) => api.post<Card>(`/api/cards/${id}/reopen`),
  remove: (id: string, confirm = false) =>
    api.delete<void>(`/api/cards/${id}?confirm=${confirm}`),
  themeSuggestion: (id: string) => api.get<CardTheme>(`/api/cards/${id}/theme-suggestion`),
};

export const sendersApi = {
  list: () => api.get<SenderMapping[]>("/api/sender-mappings"),
  create: (input: SenderMappingInput) =>
    api.post<SenderMapping>("/api/sender-mappings", input),
  update: (id: string, input: Partial<SenderMappingInput>) =>
    api.patch<SenderMapping>(`/api/sender-mappings/${id}`, input),
  remove: (id: string) => api.delete<void>(`/api/sender-mappings/${id}`),
  test: (address: string) =>
    api.get<SenderTestResult>(`/api/sender-mappings/test?address=${encodeURIComponent(address)}`),
};

export const metaApi = {
  info: () => api.get<AppInfo>("/api/info"),
  health: () => api.get<Health>("/health"),
};

// --- Auth (§14) --------------------------------------------------------------
// The status call is the one API route that answers without a session, so the
// SPA can decide whether to show the login screen at all.

export interface AuthStatus {
  auth_enabled: boolean;
  authenticated: boolean;
  /** Returned once, to the Android app only, at login. Never cached. */
  token?: string | null;
}

/** Keep the app's token out of anything that gets cached or rendered. */
function withoutToken({ token: _token, ...status }: AuthStatus): AuthStatus {
  return status;
}

export const authApi = {
  status: async () => {
    const status = await api.get<AuthStatus>("/api/auth/status");
    // A token the server no longer accepts (signed out from Settings → Devices,
    // or expired) is dropped, so the next sign-in starts clean.
    if (status.auth_enabled && !status.authenticated) {
      if (authToken()) await setAuthToken(null);
      forgetAll(); // and no saved figures outlive the session (M5)
    }
    return status;
  },
  login: async (password: string) => {
    // The phone's model names it in Settings → Devices. A browser sends
    // exactly what it always did: the password and nothing else.
    const name = await deviceName();
    const status = await api.post<AuthStatus>(
      "/api/auth/login",
      name ? { password, device_name: name } : { password },
    );
    if (status.token) await setAuthToken(status.token);
    return withoutToken(status);
  },
  logout: async () => {
    try {
      return await api.post<AuthStatus>("/api/auth/logout");
    } finally {
      await setAuthToken(null);
      forgetAll(); // the saved figures and the widget leave with the session (M5, M7)
    }
  },
};

/** A phone signed in to MyDeck (Settings → Devices). */
export interface SignedInDevice {
  id: string;
  label: string;
  client: string;
  signed_in_at: string;
  last_seen_at: string | null;
  /** The phone making this request. */
  current: boolean;
}

// --- Notifications (mobile M6) ------------------------------------------------

export type PushEvent = "transaction" | "statement" | "review" | "due_soon" | "rate_drop";

export interface PushConfig {
  /** Firebase is set up on the server (the Android app can be reached). */
  fcm: boolean;
  /** The VAPID public key, or null when Web Push isn't set up on the server. */
  webpush_public_key: string | null;
  events: Record<PushEvent, boolean>;
  event_labels: Record<PushEvent, string>;
  show_amounts: boolean;
  subscriptions: number;
}

export const pushApi = {
  config: () => api.get<PushConfig>("/api/push/config"),
  preferences: (body: { events?: Partial<Record<PushEvent, boolean>>; show_amounts?: boolean }) =>
    api.put<PushConfig>("/api/push/preferences", body),
  subscribe: (kind: "fcm" | "webpush", target: string, label?: string) =>
    api.post<{ ok: boolean }>("/api/push/subscriptions", { kind, target, label }),
  unsubscribe: (target: string) => api.delete<{ ok: boolean }>("/api/push/subscriptions", { target }),
  test: () => api.post<{ ok: boolean }>("/api/push/test"),
};

export const devicesApi = {
  list: () => api.get<SignedInDevice[]>("/api/auth/devices"),
  signOut: (id: string) => api.delete<void>(`/api/auth/devices/${encodeURIComponent(id)}`),
};

// --- Dashboard (§13.3) -------------------------------------------------------

/** Due-date colour coding (§12), plus `unconfirmed` — see the dashboard router. */
export type Urgency = "overdue" | "unconfirmed" | "soon" | "near" | "later" | "paid";
export type PaymentStatus = "paid" | "partial" | "unpaid" | "unknown";

export interface CardSummary {
  card_id: string;
  issuer: string;
  nickname: string | null;
  product_name: string | null;
  last4: string;
  theme: CardTheme | null;
  credit_limit: string | null;
  available_limit: string | null;
  statement_id: string | null;
  statement_date: string | null;
  outstanding: string | null;
  minimum_due: string | null;
  due_date: string | null;
  payment_status: PaymentStatus;
  payment_status_basis: string | null;
  is_override: boolean;
  urgency: Urgency;
  statement_count: number;
}

export interface RunwayEntry {
  card_id: string;
  statement_id: string;
  nickname: string | null;
  last4: string;
  theme: CardTheme | null;
  due_date: string;
  amount: string | null;
  urgency: Urgency;
  day_offset: number;
}

/** A figure that isn't available yet, with the honest reason. Never a zero. */
/**
 * A figure assembled from transaction alerts rather than statements (§10.4).
 *
 * `value` is null — with a reason in `unlocks` — when nothing could be
 * measured, never a confident ₹0. `caveat` travels with the number because the
 * live view must always be labelled "as seen from alerts": banks skip alerts
 * for some transaction types and thresholds, so this is a floor, not a total.
 */
export interface LiveFigure {
  value: string | null;
  unlocks: string | null;
  txn_count: number;
  provisional_count: number;
  cards_counted: number;
  cards_unknown: number;
  caveat: string;
}

export interface PendingFigure {
  value: null;
  unlocks: string;
}

/**
 * A cycle close or a due date about to land (§13.3).
 *
 * `projected` is false ONLY for a due date the issuer has already printed on a
 * statement. Everything else on this strip is arithmetic and has to be styled as
 * an estimate (§2.7) — a projected close rendered like a printed date is the app
 * claiming the bank told it something it worked out itself.
 */
export interface CycleEvent {
  on: string;
  kind: "closes" | "due";
  card_id: string;
  last4: string;
  nickname: string | null;
  theme: CardTheme | null;
  projected: boolean;
  amount: string | null;
  day_offset: number;
}

/**
 * One card's share of what is due by month end. `printed` is a bill on a
 * statement; `building` is alert spend on a cycle whose *projected* bill lands
 * this month — provisional, and styled live, never gold.
 */
export interface MonthEndLine {
  card_id: string;
  last4: string;
  nickname: string | null;
  theme: CardTheme | null;
  kind: "printed" | "building";
  amount: string;
  due_on: string;
  projected: boolean;
  cycle_start: string | null;
  cycle_end: string | null;
  txn_count: number;
}

export interface MonthEndDue {
  month_end: string;
  /** Statement truth: printed bills due today → month end, not marked paid. */
  printed_total: string;
  printed_count: number;
  /** From alerts. Never add this to `printed_total`. */
  building_total: string;
  building_count: number;
  /** Cycles due this month with nothing seen yet — unknown, not ₹0. */
  building_cards_unknown: number;
  lines: MonthEndLine[];
}

export interface DashboardSummary {
  today: string;
  fiscal_year: string;
  total_outstanding: string;
  outstanding_card_count: number;
  due_next_7_days: string;
  due_next_7_days_count: number;
  overdue_total: string;
  overdue_count: number;
  unconfirmed_total: string;
  unconfirmed_count: number;
  due_by_month_end: MonthEndDue;
  /** Assembled from alerts, not statements — see `LiveFigure`. */
  cycle_spend: LiveFigure;
  /** Statement truth, from the ledger (§9). */
  real_cashback_fy: string;
  runway_days: number;
  runway: RunwayEntry[];
  cards: CardSummary[];
  /** The next few closes and due dates — see `CycleEvent`. */
  cycle_events: CycleEvent[];
  statements_total: number;
  needs_review: number;
}

export const dashboardApi = {
  summary: () => api.get<DashboardSummary>("/api/dashboard"),
};

// --- Billing cycles ----------------------------------------------------------

/**
 * How much a card's learned cycle rules can be trusted.
 *
 * `observed` — three or more statements agree on the rule.
 * `weak`     — one or two statements, or they disagree, or the value came from
 *              the card record rather than a parsed statement.
 * `unknown`  — nothing to learn from; the projection is absent, not guessed.
 */
export type CycleConfidence = "observed" | "weak" | "unknown";

/**
 * One billing cycle on a card's timeline. Projected unless `billed` (its
 * statement is in hand) — and then `due_printed` says the due date is the
 * issuer's own.
 */
export interface CyclePlan {
  start: string;
  end: string;
  statement_on: string | null;
  due_on: string | null;
  billed: boolean;
  due_printed: boolean;
}

export interface CycleCard {
  card_id: string;
  issuer: string;
  nickname: string | null;
  product_name: string | null;
  last4: string;
  theme: CardTheme | null;

  // Printed by the issuer — fact.
  last_statement_date: string | null;
  last_period_start: string | null;
  last_period_end: string | null;
  outstanding: string | null;
  outstanding_due_date: string | null;
  outstanding_status: PaymentStatus;

  // The cycle open right now.
  cycle_start: string | null;
  cycle_end: string | null;
  cycle_length_days: number | null;
  days_into_cycle: number | null;
  days_left_in_cycle: number | null;

  /**
   * Projections. Arithmetic from the card's own history, never a printed date —
   * `due_expected_on` and `outstanding_due_date` are deliberately separate
   * fields so the UI cannot render one as the other.
   */
  statement_expected_on: string | null;
  due_expected_on: string | null;
  grace_days: number | null;
  /** Interest-free days a purchase made today would get. */
  float_days: number | null;

  close_basis: string;
  due_basis: string;
  confidence: CycleConfidence;
  statement_count: number;
  /** Closes that passed with something to bill and no statement. A real gap. */
  statements_missing: number;
  /**
   * Closes that passed with nothing to bill — no activity, no balance carried in.
   * An issuer sends no statement for one of these, so it is expected, not a gap.
   */
  quiet_cycles: number;
  unavailable: string | null;
  /** Last closed cycle, the open one, then two more — oldest first. */
  path: CyclePlan[];
}

export interface CyclesResponse {
  today: string;
  /** Soonest to close first. */
  cards: CycleCard[];
  events: CycleEvent[];
  float_caveat: string;
}

export const cyclesApi = {
  list: () => api.get<CyclesResponse>("/api/cycles"),
};

// --- Transactions (§13.3) ----------------------------------------------------

export interface Transaction {
  id: string;
  card_id: string;
  statement_id: string | null;
  txn_date: string;
  post_date: string | null;
  description_raw: string;
  merchant: string | null;
  amount: string;
  direction: "debit" | "credit";
  category_id: string | null;
  flags: Record<string, string> | null;
  source: string;
  is_provisional: boolean;
  created_at: string;
}

export interface TransactionPage {
  items: Transaction[];
  total: number;
  offset: number;
  limit: number;
  total_debits: string;
  total_credits: string;
}

export interface TransactionFilters {
  card_id?: string[];
  date_from?: string;
  date_to?: string;
  direction?: "debit" | "credit";
  category_id?: string;
  flag?: string;
  provisional_only?: boolean;
  search?: string;
  offset?: number;
  limit?: number;
}

function transactionQuery(f: TransactionFilters): string {
  const q = new URLSearchParams();
  for (const id of f.card_id ?? []) q.append("card_id", id);
  if (f.date_from) q.set("date_from", f.date_from);
  if (f.date_to) q.set("date_to", f.date_to);
  if (f.direction) q.set("direction", f.direction);
  if (f.category_id) q.set("category_id", f.category_id);
  if (f.flag) q.set("flag", f.flag);
  if (f.provisional_only) q.set("provisional_only", "true");
  if (f.search?.trim()) q.set("search", f.search.trim());
  if (f.offset !== undefined) q.set("offset", String(f.offset));
  if (f.limit !== undefined) q.set("limit", String(f.limit));
  return q.toString();
}

export const transactionsApi = {
  list: (filters: TransactionFilters = {}) =>
    api.get<TransactionPage>(`/api/transactions?${transactionQuery(filters)}`),
  setCategory: (id: string, categoryId: string | null) =>
    api.patch<Transaction>(`/api/transactions/${id}`, { category_id: categoryId }),
  exportUrl: (filters: TransactionFilters = {}) =>
    `/api/transactions/export.csv?${transactionQuery({ ...filters, offset: undefined, limit: undefined })}`,
};

// --- Real Cashback Ledger (§9) -----------------------------------------------

export type CashbackEntryType =
  | "statement_credit"
  /** The bank taking cashback back. Stored positive, subtracted when totalled. */
  | "statement_clawback"
  | "external_realization";

export interface CashbackEntry {
  id: string;
  card_id: string;
  entry_type: CashbackEntryType;
  amount_inr: string;
  entry_date: string;
  fiscal_year: string;
  linked_transaction_id: string | null;
  description: string | null;
  evidence_note: string | null;
  created_by: "auto" | "manual";
  created_at: string;
}

export interface CardBreakdown {
  card_id: string;
  nickname: string | null;
  product_name: string | null;
  issuer: string;
  last4: string;
  theme: CardTheme | null;
  /** The user's own account of how this card rewards spending — shown beside a
   *  0.00% rate so it reads as a fact rather than a verdict. */
  rewards_note: string | null;
  real_cashback: string;
  /** Cashback the bank took back. Already subtracted from `real_cashback`. */
  clawbacks: string;
  realized_rewards: string;
  eligible_spend: string;
  fees: string;
  interest: string;
  entry_count: number;
  effective_rate: string | null;
  unexplained_count: number;
  /** The FY rate survives a one-cycle lag; the reader should still know which
   *  cards pay in arrears. */
  credit_timing: CreditTiming;
}

export interface LedgerSummary {
  fiscal_year: string;
  fiscal_years: string[];
  period_start: string;
  period_end: string;
  real_cashback: string;
  clawbacks: string;
  lifetime_real_cashback: string;
  realized_rewards: string;
  eligible_spend: string;
  effective_rate: string | null;
  fees: string;
  interest: string;
  entry_count: number;
  unexplained_count: number;
  unexplained_total: string;
  cards: CardBreakdown[];
}

export interface UnexplainedCredit {
  transaction_id: string;
  card_id: string;
  txn_date: string;
  amount: string;
  description: string;
}

/**
 * Money back that was NOT earned on spend, so it never joins the headline (§9).
 * Kept as its own shape rather than folded into `LedgerSummary` so there is no
 * tempting `real_cashback + benefits` field for anyone to reach for.
 */
export interface FuelSurcharge {
  levied: string;
  waived: string;
  net_cost: string;
  levied_count: number;
  waived_count: number;
  waived_share: string | null;
  fuel_spend: string;
  fuel_txn_count: number;
  /** Fuel spend on cards that actually levy the surcharge — the rate's denominator. */
  surcharged_fuel_spend: string;
  observed_rate: string | null;
  /** `false` when a surcharge line has no fuel purchase behind it, i.e. the
   *  merchant rules are missing an outlet and `fuel_spend` understates.
   *  `null` when nothing was levied — no check to pass, rather than a pass. */
  reconciled: boolean | null;
  unmatched_surcharges: number;
  unmatched_surcharge_amount: string;
}

export interface FuelCard extends FuelSurcharge {
  card_id: string;
  nickname: string | null;
  product_name: string | null;
  issuer: string;
  last4: string;
}

export interface FeeReversalCard {
  card_id: string;
  nickname: string | null;
  product_name: string | null;
  issuer: string;
  last4: string;
  amount: string;
  count: number;
  descriptions: string[];
}

export interface Benefits {
  fiscal_year: string;
  total: string;
  fuel: FuelSurcharge;
  fuel_cards: FuelCard[];
  fee_reversals: string;
  fee_reversal_count: number;
  fee_reversal_cards: FeeReversalCard[];
}

/**
 * One calendar month of the same ledger the FY headline totals. An FY's months
 * add up to its headline exactly — the backend derives both from one function
 * so the two figures can never drift.
 */
/**
 * How a card's cashback arrives, measured from its own statements.
 *
 * `cycle_credit` means one credit per statement cycle, for the cycle *before*
 * it: that month's credit was earned on the previous month's spend, so a
 * same-month rate is not a rate. Which cards these are is measured from their
 * own statements at runtime, never listed in code (§2.1).
 */
export type CreditTiming = "cycle_credit" | "per_transaction" | "unknown";

export interface MonthCard {
  card_id: string;
  nickname: string | null;
  product_name: string | null;
  issuer: string;
  last4: string;
  real_cashback: string;
  clawbacks: string;
  eligible_spend: string;
  entry_count: number;
  /** `null` for a `cycle_credit` card — withheld, not zero. */
  effective_rate: string | null;
  credit_timing: CreditTiming;
}

export interface CashbackMonth {
  /** "2026-07" — formatted for display by `formatMonth`, never by the API. */
  month: string;
  fiscal_year: string;
  start: string;
  end: string;
  real_cashback: string;
  /** Gross credits, before clawbacks — what tells a quiet month apart from a
   *  month that was clawed back to nothing. */
  credits: string;
  clawbacks: string;
  realized_rewards: string;
  eligible_spend: string;
  fees: string;
  interest: string;
  entry_count: number;
  effective_rate: string | null;
  /** A card paying a cycle in arrears earned in this month, so the rate beside
   *  it pairs credits with spend from different periods. */
  timing_sensitive: boolean;
  cards: MonthCard[];
}

export type MonthlyScope = "fiscal_year" | "lifetime";

export interface MonthlySummary {
  scope: MonthlyScope;
  fiscal_year: string | null;
  fiscal_years: string[];
  card_id: string | null;
  months: CashbackMonth[];
  total: string;
  clawbacks: string;
  eligible_spend: string;
  effective_rate: string | null;
  timing_sensitive: boolean;
  month_count: number;
  /** Months that actually earned something — the divisor for an average that
   *  isn't dragged down by months before the first card existed. */
  active_months: number;
  average_per_month: string;
  average_per_active_month: string | null;
  best_month: string | null;
  best_month_amount: string;
  /** Latest month minus the one before it. `null` with nothing to compare to. */
  latest_change: string | null;
}

export interface ManualEntryInput {
  card_id: string;
  amount_inr: string;
  entry_date: string;
  description: string;
  evidence_note?: string | null;
}

/** Lifetime ignores the FY entirely — sending one anyway invites a stale filter. */
function monthlyQuery(scope: MonthlyScope, fiscalYear?: string, cardId?: string | null): string {
  const params = new URLSearchParams({ scope });
  if (scope === "fiscal_year" && fiscalYear) params.set("fiscal_year", fiscalYear);
  if (cardId) params.set("card_id", cardId);
  return params.toString();
}

export const cashbackApi = {
  summary: (fiscalYear?: string) =>
    api.get<LedgerSummary>(
      `/api/cashback${fiscalYear ? `?fiscal_year=${encodeURIComponent(fiscalYear)}` : ""}`,
    ),
  entries: (fiscalYear?: string) =>
    api.get<CashbackEntry[]>(
      `/api/cashback/entries${fiscalYear ? `?fiscal_year=${encodeURIComponent(fiscalYear)}` : ""}`,
    ),
  unexplained: (fiscalYear?: string) =>
    api.get<UnexplainedCredit[]>(
      `/api/cashback/unexplained${fiscalYear ? `?fiscal_year=${encodeURIComponent(fiscalYear)}` : ""}`,
    ),
  benefits: (fiscalYear?: string) =>
    api.get<Benefits>(
      `/api/cashback/benefits${fiscalYear ? `?fiscal_year=${encodeURIComponent(fiscalYear)}` : ""}`,
    ),
  monthly: (scope: MonthlyScope, fiscalYear?: string, cardId?: string | null) =>
    api.get<MonthlySummary>(`/api/cashback/monthly?${monthlyQuery(scope, fiscalYear, cardId)}`),
  monthlyExportUrl: (scope: MonthlyScope, fiscalYear?: string, cardId?: string | null) =>
    `/api/cashback/monthly.csv?${monthlyQuery(scope, fiscalYear, cardId)}`,
  addManual: (input: ManualEntryInput) =>
    api.post<CashbackEntry>("/api/cashback/entries", input),
  remove: (id: string) => api.delete<void>(`/api/cashback/entries/${id}`),
  rebuild: () =>
    api.post<{ created: number; removed: number; unchanged: number }>("/api/cashback/rebuild"),
  exportUrl: (fiscalYear: string, format: "csv" | "xlsx") =>
    `/api/cashback/export.${format}?fiscal_year=${encodeURIComponent(fiscalYear)}`,
};

// --- Transaction rules (§6, §13.3) -------------------------------------------

export type TxnFlag =
  | "cashback"
  | "payment"
  | "fee"
  | "interest"
  | "emi"
  | "exclude_from_spend"
  | "fuel_surcharge";

export interface TxnRule {
  id: string;
  scope: "global" | "issuer" | "card";
  issuer: string | null;
  card_id: string | null;
  pattern: string;
  sets_flag: TxnFlag;
  priority: number;
  active: boolean;
  seeded: boolean;
  notes: string | null;
}

export interface RulePreviewMatch {
  transaction_id: string;
  card_id: string;
  card_name: string;
  txn_date: string;
  amount: string;
  direction: "debit" | "credit";
  description: string;
  already_flagged: boolean;
}

export interface RulePreview {
  valid: boolean;
  error: string | null;
  match_count: number;
  new_match_count: number;
  total_amount: string | null;
  matches: RulePreviewMatch[];
  explanation: string;
}

export const rulesApi = {
  list: () => api.get<TxnRule[]>("/api/rules"),
  preview: (pattern: string, setsFlag: TxnFlag) =>
    api.get<RulePreview>(
      `/api/rules/preview?pattern=${encodeURIComponent(pattern)}&sets_flag=${setsFlag}`,
    ),
  create: (input: { pattern: string; sets_flag: TxnFlag; priority?: number; notes?: string }) =>
    api.post<TxnRule>("/api/rules", input),
  update: (id: string, input: Partial<{ pattern: string; sets_flag: TxnFlag; active: boolean }>) =>
    api.patch<TxnRule>(`/api/rules/${id}`, input),
  remove: (id: string) => api.delete<void>(`/api/rules/${id}`),
};

// --- Email sync (§3.2) -------------------------------------------------------

export interface SyncRun {
  id: string;
  started_at: string | null;
  finished_at: string | null;
  source: "scheduled" | "manual" | "upload";
  emails_seen: number;
  statements_ingested: number;
  alerts_ingested: number;
  review_items_created: number;
  ok: boolean;
  error: string | null;
}

export interface SyncStatus {
  mode: "imap" | "gmail_api" | "disabled";
  configured: boolean;
  mailbox: string | null;
  interval_minutes: number;
  /** False while the .env starting value is still in use. */
  interval_saved: boolean;
  /** When the next automatic check is due; null when nothing is scheduled. */
  next_run_at: string | null;
  last_run: SyncRun | null;
  /** Only set by an explicit connection test — status polls don't dial out. */
  connection_ok: boolean | null;
  detail: string | null;
}

export interface SyncResult {
  ok: boolean;
  emails_seen: number;
  statements_ingested: number;
  alerts_ingested: number;
  review_items_created: number;
  duplicates: number;
  error: string | null;
  message: string;
}

export const syncApi = {
  status: () => api.get<SyncStatus>("/api/sync/status"),
  test: () => api.post<SyncStatus>("/api/sync/test"),
  /**
   * `full` also retries parked mail waiting on a PDF password, a card or a
   * parser. Mail from a newly mapped sender is retried by every sync anyway.
   */
  run: (full = false) => api.post<SyncResult>(`/api/sync/run?full=${full}`),
  runs: (limit = 20) => api.get<SyncRun[]>(`/api/sync/runs?limit=${limit}`),
  /** How often to check the inbox. Applies at once — no restart. */
  setInterval: (minutes: number) => api.put<SyncStatus>("/api/sync/interval", { minutes }),
};

/** Mirrors `services/sync_schedule.py` — 5 minutes to 24 hours. */
export const SYNC_INTERVAL_BOUNDS = { min: 5, max: 24 * 60 } as const;

// --- Statements --------------------------------------------------------------

export interface Statement {
  id: string;
  card_id: string;
  statement_date: string | null;
  period_start: string | null;
  period_end: string | null;
  payment_due_date: string | null;
  total_amount_due: string | null;
  minimum_amount_due: string | null;
  opening_balance: string | null;
  closing_balance: string | null;
  credit_limit: string | null;
  available_limit: string | null;
  reward_points_summary: Record<string, string> | null;
  parse_status: "parsed" | "partial" | "failed" | "manual";
  needs_review: boolean;
  review_notes: string | null;
  payment_status: PaymentStatus;
  payment_status_override: PaymentStatus | null;
  payment_status_basis: string | null;
  parser_name: string | null;
  parser_version: string | null;
  created_at: string;
  transaction_count: number;
}

export interface UploadResult {
  ok: boolean;
  duplicate: boolean;
  statement_id: string | null;
  card_id: string | null;
  needs_review: boolean;
  transaction_count: number;
  parser_name: string | null;
  message: string;
  warnings: string[] | null;
  /** Additive (mobile M7): the question this upload raised, e.g. a locked PDF. */
  review_item_id?: string | null;
  review_type?: string | null;
}

export interface ReviewItem {
  id: string;
  type:
    | "parse_failed"
    | "unknown_sender"
    | "unknown_card"
    | "no_attachment"
    | "password_needed"
    | "unreconciled_alert"
    | "alert_parse_failed";
  payload: Record<string, unknown> | null;
  resolved: boolean;
  created_at: string;
}

export interface StatementCorrection {
  statement_date?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  payment_due_date?: string | null;
  total_amount_due?: string | null;
  minimum_amount_due?: string | null;
  opening_balance?: string | null;
  closing_balance?: string | null;
  credit_limit?: string | null;
  available_limit?: string | null;
  card_id?: string | null;
}

export const statementsApi = {
  list: (params?: { card_id?: string; needs_review?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.card_id) q.set("card_id", params.card_id);
    if (params?.needs_review !== undefined) q.set("needs_review", String(params.needs_review));
    const qs = q.toString();
    return api.get<Statement[]>(`/api/statements${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => api.get<Statement>(`/api/statements/${id}`),
  text: (id: string) => api.get<{ text: string; pages: string[] }>(`/api/statements/${id}/text`),
  update: (id: string, body: StatementCorrection) =>
    api.patch<Statement>(`/api/statements/${id}`, body),
  reparse: (id: string) => api.post<UploadResult>(`/api/statements/${id}/reparse`),
  remove: (id: string) => api.delete<void>(`/api/statements/${id}`),
  reviewQueue: () => api.get<ReviewItem[]>("/api/statements/review-queue"),
  /** Close a review item the user has checked and decided needs nothing. */
  dismissReview: (id: string) =>
    api.post<void>(`/api/statements/review-queue/${id}/dismiss`),
  /** Override the derived payment status, or "auto" to clear it (§12). */
  setPaymentStatus: (id: string, status: string) =>
    api.post<Statement>(`/api/statements/${id}/payment-status`, { status }),
  /** Supply a password for a statement MyDeck couldn't open (rotating-password issuers). */
  unlock: (reviewItemId: string, password: string) =>
    api.post<UploadResult>("/api/statements/unlock", {
      review_item_id: reviewItemId,
      password,
    }),

  /** Multipart — deliberately not routed through `api`, which assumes JSON. */
  upload: async (file: File): Promise<UploadResult> => {
    const body = new FormData();
    body.append("file", file);
    const res = await fetch(apiBase() + "/api/statements/upload", {
      method: "POST",
      body,
      credentials: "same-origin",
      headers: requestHeaders(false),
    });
    const text = await res.text();
    const parsed = text ? JSON.parse(text) : undefined;
    if (!res.ok) {
      const detail =
        typeof parsed?.detail === "string" ? parsed.detail : `Upload failed (${res.status})`;
      throw new ApiError(res.status, detail, parsed);
    }
    return parsed as UploadResult;
  },
};

// --- Reward rules, caps and the planner (§11, Phase 7) -----------------------
// Every figure below is an ESTIMATE (§2.7). Responses carry `estimated: true`
// so the UI cannot forget to mark them.

export interface Category {
  id: string;
  name: string;
  icon: string | null;
  sort: number;
}

export type CapPeriod = "calendar_month" | "statement_cycle";
export type CapRuleType = "rate_cap" | "milestone";
export type CapChannel = "any" | "online" | "offline";

export interface RuleConditions {
  category_ids?: string[];
  exclude_category_ids?: string[];
  merchant_pattern?: string | null;
  exclude_merchant_pattern?: string | null;
  channel?: CapChannel;
  min_txn_amount?: string | null;
}

export interface CapRule {
  id: string;
  card_id: string;
  name: string;
  rule_type: CapRuleType;
  conditions: RuleConditions;
  rate_percent: string | null;
  cap_amount: string | null;
  period: CapPeriod;
  milestone_target_spend: string | null;
  milestone_reward_note: string | null;
  valid_from: string;
  valid_to: string | null;
  active: boolean;
  notes: string | null;
}

export interface CapRuleInput {
  name: string;
  rule_type: CapRuleType;
  conditions: RuleConditions;
  rate_percent: string | null;
  cap_amount: string | null;
  period: CapPeriod;
  milestone_target_spend: string | null;
  milestone_reward_note: string | null;
  valid_from: string;
  valid_to: string | null;
  active: boolean;
  notes: string | null;
}

export interface RuleStatus {
  rule_id: string;
  name: string;
  rate_percent: string | null;
  cap_amount: string | null;
  period: CapPeriod;
  period_start: string | null;
  period_end: string | null;
  days_left: number | null;
  matched_spend: string;
  earned: string;
  headroom: string | null;
  txn_count: number;
  provisional_count: number;
  channel_unverified: boolean;
  uncategorised_spend: string | null;
  unavailable: string | null;
}

export interface MilestoneStatus {
  rule_id: string;
  name: string;
  period: CapPeriod;
  period_start: string | null;
  period_end: string | null;
  days_left: number | null;
  spend: string;
  target: string;
  remaining: string;
  reached: boolean;
  reward_note: string | null;
  unavailable: string | null;
}

export interface CycleRate {
  statement_id: string;
  statement_date: string;
  spend: string;
  cashback: string;
  rate_percent: string | null;
}

export interface LearnedRate {
  rate_percent: string | null;
  cashback: string;
  spend: string;
  statements: number;
  lagged: boolean;
  confidence: "observed" | "weak" | "unknown";
  basis: string;
  /** Oldest first: each statement cycle's spend and the cashback it earned. */
  cycles: CycleRate[];
  /** The last two cycles paid clearly less than the card's own earlier rate. */
  drop_warning: string | null;
  usual_rate_percent: string | null;
}

export interface EstimateVsActual {
  statement_id: string;
  statement_date: string;
  period_start: string | null;
  period_end: string | null;
  estimated: string;
  actual: string | null;
  gap: string | null;
  verdict: "match" | "under" | "over" | "pending";
  approximate: boolean;
  note: string;
}

export type RewardBasis = "rules" | "learned" | "none";

export interface CardRewards {
  card_id: string;
  issuer: string;
  nickname: string | null;
  product_name: string | null;
  last4: string;
  theme: CardTheme | null;
  rewards_note: string | null;
  basis: RewardBasis;
  rule_count: number;
  in_force: RuleStatus[];
  milestones: MilestoneStatus[];
  learned: LearnedRate;
  history: EstimateVsActual[];
  stale_warning: string | null;
  channel_caveat: string;
  estimated: true;
}

export const capsApi = {
  categories: () => api.get<Category[]>("/api/transactions/categories"),
  rules: (cardId: string) => api.get<CapRule[]>(`/api/caps/rules?card_id=${cardId}`),
  create: (cardId: string, input: CapRuleInput) =>
    api.post<CapRule>("/api/caps/rules", { ...input, card_id: cardId }),
  correct: (id: string, input: CapRuleInput) => api.patch<CapRule>(`/api/caps/rules/${id}`, input),
  revise: (id: string, input: CapRuleInput) =>
    api.post<CapRule>(`/api/caps/rules/${id}/revise`, input),
  remove: (id: string) => api.delete<void>(`/api/caps/rules/${id}`),
  wallet: () => api.get<CardRewards[]>("/api/caps/cards"),
  card: (cardId: string) => api.get<CardRewards>(`/api/caps/cards/${cardId}`),
};

export type PlanChannel = "online" | "offline" | "unknown";

export interface PlanRequest {
  amount: string;
  channel: PlanChannel;
  category_id?: string | null;
  merchant?: string | null;
}

export interface PlanCard {
  card_id: string;
  issuer: string;
  nickname: string | null;
  product_name: string | null;
  last4: string;
  theme: CardTheme | null;
  basis: RewardBasis;
  confidence: "observed" | "weak" | "unknown";
  estimated_cashback: string;
  effective_rate: string | null;
  reasons: string[];
  hints: string[];
  float_days: number | null;
}

export interface PlanResponse {
  amount: string;
  channel: PlanChannel;
  category_id: string | null;
  category_name: string | null;
  category_inferred: boolean;
  merchant: string | null;
  on: string;
  results: PlanCard[];
  caveat: string;
  estimated: true;
}

export const plannerApi = {
  plan: (req: PlanRequest) => api.post<PlanResponse>("/api/planner", req),
};
